import { FC, useState, useEffect, useMemo } from "react";
import { Select, Tag, Input, Button, Space } from "antd";
import { UserOutlined, MailOutlined, TeamOutlined } from "@ant-design/icons";
import { RecipientType } from "~/types/communication";
import { useDetailBookingStore } from "../../detail-booking.store";

type RecipientSelection = {
  recipientTypes: RecipientType[];
  recipientIds: string[];
  additionalEmails: string[];
};

type Props = {
  value?: RecipientSelection;
  onChange?: (value: RecipientSelection) => void;
};

export const RecipientSelector: FC<Props> = ({ value, onChange }) => {
  const { booking } = useDetailBookingStore();
  const [recipientTypes, setRecipientTypes] = useState<RecipientType[]>(value?.recipientTypes || []);
  const [recipientIds, setRecipientIds] = useState<string[]>(value?.recipientIds || []);
  const [additionalEmails, setAdditionalEmails] = useState<string[]>(value?.additionalEmails || []);
  const [emailInput, setEmailInput] = useState("");

  // Get clients from the current booking (booking client + service clients)
  const bookingClients = useMemo(() => {
    if (!booking) return [];

    const clients = [];

    // Add main booking client
    if (booking.client) {
      clients.push(booking.client);
    }

    // Add service clients from individual service tickets
    if (booking.serviceTicket) {
      booking.serviceTicket.forEach((service: any) => {
        if (service.client && !clients.find(c => c.id === service.client.id)) {
          clients.push(service.client);
        }
      });
    }

    return clients;
  }, [booking]);

  // Get staff assigned to services in this booking
  const assignedStaff = useMemo(() => {
    if (!booking?.serviceTicket) return [];

    const staff = [];

    booking.serviceTicket.forEach((service: any) => {
      if (service.staffs) {
        service.staffs.forEach((staffMember: any) => {
          if (staffMember && !staff.find(s => s.id === staffMember.id)) {
            staff.push(staffMember);
          }
        });
      }
    });

    return staff;
  }, [booking]);

  // Update parent when values change
  useEffect(() => {
    onChange?.({
      recipientTypes,
      recipientIds,
      additionalEmails,
    });
  }, [recipientTypes, recipientIds, additionalEmails]);

  const recipientTypeOptions = [
    {
      label: "Booking Client",
      value: RecipientType.BOOKING_CLIENT,
      icon: <UserOutlined />,
      description: booking?.client?.email ? `${booking.client.companyName || `${booking.client.firstName} ${booking.client.lastName}`} (${booking.client.email})` : "Main client for this booking",
    },
    {
      label: "Service Clients",
      value: RecipientType.SERVICE_CLIENTS,
      icon: <UserOutlined />,
      description: "Clients from individual service tickets",
    },
    {
      label: "Assigned Staff",
      value: RecipientType.ASSIGNED_STAFF,
      icon: <TeamOutlined />,
      description: `Staff assigned to services in this booking (${assignedStaff.length} staff)`,
    },
  ];

  const handleRecipientTypeChange = (types: RecipientType[]) => {
    setRecipientTypes(types);
  };

  const handleAddEmail = () => {
    const email = emailInput.trim();
    if (email && !additionalEmails.includes(email)) {
      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (emailRegex.test(email)) {
        setAdditionalEmails([...additionalEmails, email]);
        setEmailInput("");
      }
    }
  };

  const handleRemoveEmail = (email: string) => {
    setAdditionalEmails(additionalEmails.filter(e => e !== email));
  };

  const clientOptions = bookingClients.map((client: any) => ({
    label: `${client.companyName || `${client.firstName} ${client.lastName}`}${client.email ? ` (${client.email})` : ''}`,
    value: client.id,
  }));

  const staffOptions = assignedStaff.map((staff: any) => ({
    label: `${staff.user?.firstName || ''} ${staff.user?.lastName || ''}${staff.user?.email ? ` (${staff.user.email})` : ''}`.trim() || 'Unknown Staff',
    value: staff.id,
  }));

  const getRecipientTypeLabel = (type: RecipientType) => {
    const option = recipientTypeOptions.find(opt => opt.value === type);
    return option?.label || type;
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Recipient Groups
        </label>
        <Select
          mode="multiple"
          style={{ width: '100%' }}
          placeholder="Select recipient groups (e.g., All Staff, Booking Client)"
          value={recipientTypes}
          onChange={handleRecipientTypeChange}
          options={recipientTypeOptions.map(opt => ({
            label: (
              <div className="flex items-center">
                <span className="mr-2">{opt.icon}</span>
                <div>
                  <div>{opt.label}</div>
                  <div className="text-xs text-gray-500">{opt.description}</div>
                </div>
              </div>
            ),
            value: opt.value,
          }))}
          optionLabelProp="label"
        />
        {recipientTypes.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {recipientTypes.map(type => (
              <Tag
                key={type}
                closable
                onClose={() => handleRecipientTypeChange(recipientTypes.filter(t => t !== type))}
              >
                {getRecipientTypeLabel(type)}
              </Tag>
            ))}
          </div>
        )}
      </div>

      {clientOptions.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Specific Clients (from this booking)
          </label>
          <Select
            mode="multiple"
            style={{ width: '100%' }}
            placeholder="Select specific clients"
            value={recipientIds.filter(id => clientOptions.some(opt => opt.value === id))}
            onChange={(ids) => {
              // Keep existing staff IDs and add new client IDs
              const staffIds = recipientIds.filter(id =>
                staffOptions.some(opt => opt.value === id)
              );
              setRecipientIds([...staffIds, ...ids]);
            }}
            options={clientOptions}
            showSearch
            filterOption={(input, option) =>
              (option?.label?.toString() || '').toLowerCase().includes(input.toLowerCase())
            }
          />
        </div>
      )}

      {staffOptions.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Specific Staff (assigned to this booking)
          </label>
          <Select
            mode="multiple"
            style={{ width: '100%' }}
            placeholder="Select specific staff"
            value={recipientIds.filter(id => staffOptions.some(opt => opt.value === id))}
            onChange={(ids) => {
              // Keep existing client IDs and add new staff IDs
              const clientIds = recipientIds.filter(id =>
                clientOptions.some(opt => opt.value === id)
              );
              setRecipientIds([...clientIds, ...ids]);
            }}
            options={staffOptions}
            showSearch
            filterOption={(input, option) =>
              (option?.label?.toString() || '').toLowerCase().includes(input.toLowerCase())
            }
          />
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Additional Email Addresses
        </label>
        <Space.Compact style={{ width: '100%' }}>
          <Input
            placeholder="Enter email address"
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            onPressEnter={handleAddEmail}
            prefix={<MailOutlined />}
          />
          <Button type="primary" onClick={handleAddEmail}>
            Add
          </Button>
        </Space.Compact>
        {additionalEmails.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {additionalEmails.map(email => (
              <Tag
                key={email}
                closable
                onClose={() => handleRemoveEmail(email)}
                icon={<MailOutlined />}
              >
                {email}
              </Tag>
            ))}
          </div>
        )}
      </div>

      {/* Summary */}
      {(recipientTypes.length > 0 || recipientIds.length > 0 || additionalEmails.length > 0) && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded">
          <div className="text-sm font-medium text-blue-900 mb-2">
            Message will be sent to:
          </div>
          <ul className="text-sm text-blue-800 space-y-1">
            {recipientTypes.map(type => (
              <li key={type}>• {getRecipientTypeLabel(type)}</li>
            ))}
            {recipientIds.length > 0 && (
              <li>• {recipientIds.length} specific individual{recipientIds.length !== 1 ? 's' : ''}</li>
            )}
            {additionalEmails.length > 0 && (
              <li>• {additionalEmails.length} additional email{additionalEmails.length !== 1 ? 's' : ''}</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};
